import { createFileRoute, redirect } from "@tanstack/react-router";
import { ListOrderedIcon, PlusIcon } from "lucide-react";
import { Suspense, useState } from "react";
import z from "zod";

import { isAdminRole, roleHasPermission } from "@/auth/shared";
import { Button } from "@/components/base/button";
import { title } from "@/components/base/primitives";
import { ArticleForm } from "@/components/help-articles/article-form";
import { ArticleList } from "@/components/help-articles/article-list";
import { ArticleSearch } from "@/components/help-articles/article-search";
import { ReorderArticles } from "@/components/help-articles/reorder-articles";
import { TagFilter } from "@/components/help-articles/tag-filter";
import { DefaultLayout } from "@/layouts/default";

const helpSearchSchema = z.object({
  search: z.string().optional(),
  tag: z.string().optional(),
});

export const Route = createFileRoute("/td/help/")({
  validateSearch: helpSearchSchema,
  loaderDeps: ({ search }) => search,
  loader: async ({ context }) => {
    const viewer = context.viewer;

    const hasPermission =
      viewer &&
      roleHasPermission(viewer.role, {
        tournaments: ["update"],
      });

    if (!hasPermission) {
      throw redirect({ to: "/not-found" });
    }
  },
  component: RouteComponent,
});

function RouteComponent() {
  const search = Route.useSearch();
  const context = Route.useRouteContext();
  const viewer = context.viewer;
  const isAdmin = isAdminRole(viewer?.role);

  const [isCreating, setIsCreating] = useState(false);

  return (
    <DefaultLayout
      classNames={{
        content:
          "flex flex-col space-y-6 w-full max-w-full md:max-w-3xl px-3 py-12 mx-auto",
      }}
    >
      <div className="flex items-center justify-between">
        <h1 className={title({ size: "sm" })}>Help Articles</h1>
        {isAdmin && (
          <div className="flex gap-2">
            <ReorderArticles
              trigger={
                <Button size="sm" variant="outline">
                  <ListOrderedIcon size={14} />
                  Reorder
                </Button>
              }
            />
            <Button
              size="sm"
              color="primary"
              onPress={() => setIsCreating(true)}
            >
              <PlusIcon size={14} />
              New Article
            </Button>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
        <ArticleSearch defaultValue={search.search} />
      </div>

      <Suspense fallback={<p className="text-gray-500">Loading tags...</p>}>
        <TagFilter activeTag={search.tag} />
      </Suspense>

      <Suspense fallback={<p className="text-gray-500">Loading articles...</p>}>
        <ArticleList search={search.search} tag={search.tag} />
      </Suspense>

      {isCreating && (
        <ArticleForm isOpen={isCreating} onOpenChange={setIsCreating} />
      )}
    </DefaultLayout>
  );
}
